import { lazy, type ComponentType } from "react";
import type { SlideProps } from "@/decks/types";

type Load = () => Promise<{ default: ComponentType<SlideProps> }>;

const cache = new Map<string, ComponentType<SlideProps>>();
const preloaded = new Map<string, ComponentType<SlideProps>>();

// Descarga el chunk de una slide antes de navegar a ella. Si termina antes del primer
// render, getLazySlide devuelve el componente real y la slide no suspende: un lazy()
// suspende siempre en su primer render aunque el chunk ya esté bajado, y React retiene
// el fallback ~300 ms, que se ve como un hueco al entrar la slide.
export function preloadSlide(deckId: string, slideId: string, load: Load) {
  const key = `${deckId}/${slideId}`;
  if (cache.has(key) || preloaded.has(key)) return;
  void load().then(
    (module) => preloaded.set(key, module.default),
    () => undefined,
  );
}

// La identidad del componente queda fija por slide desde su primer uso, para que no se
// remonte (y reinicie sus animaciones) si el chunk termina de bajar mientras está visible.
export function getLazySlide(
  deckId: string,
  slideId: string,
  load: Load,
): ComponentType<SlideProps> {
  const key = `${deckId}/${slideId}`;
  let component = cache.get(key);
  if (!component) {
    component = preloaded.get(key) ?? lazy(load);
    cache.set(key, component);
  }
  return component;
}
